import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import {
  linkParentToChild,
  getLinkedChildren,
  getChildProgress,
  subscribeToChildProgress,
  unlinkChild,
  getChildUpcomingEvents
} from '../../services/parentService';
import ParentChildMessaging from './ParentChildMessaging';
import './ParentDashboard.css';

const ParentDashboard = () => {
  const { user } = useUser();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childProgress, setChildProgress] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkMessage, setLinkMessage] = useState({ type: '', text: '' });
  const [showLinkForm, setShowLinkForm] = useState(false);

  const loadChildren = async () => {
    try {
      setLoading(true);
      const linkedChildren = await getLinkedChildren(user.id);
      setChildren(linkedChildren);
      if (linkedChildren.length > 0 && !selectedChild) {
        setSelectedChild(linkedChildren[0]);
      }
    } catch (error) {
      console.error('Error loading children:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.id) {
      loadChildren();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (selectedChild) {
      loadChildData(selectedChild.id);
      const unsubscribe = subscribeToChildProgress(user.id, selectedChild.id, (progress) => {
        setChildProgress(progress);
      });
      return () => unsubscribe();
    }
  }, [selectedChild, user]);

  const loadChildData = async (childId) => {
    try {
      const progress = await getChildProgress(childId);
      setChildProgress(progress);
      
      const events = await getChildUpcomingEvents(childId);
      setUpcomingEvents(events);
    } catch (error) {
      console.error('Error loading child data:', error);
    }
  };

  const handleLinkChild = async (e) => {
    e.preventDefault();
    if (!linkEmail.trim()) {
      setLinkMessage({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }

    setLinking(true);
    setLinkMessage({ type: '', text: '' });

    const result = await linkParentToChild(user.id, linkEmail.trim());
    
    if (result.success) {
      setLinkMessage({ type: 'success', text: result.message });
      setLinkEmail('');
      setShowLinkForm(false);
      await loadChildren();
      if (result.childId) {
        const newChild = children.find(c => c.id === result.childId) || 
          { id: result.childId, name: result.childName, email: linkEmail };
        setSelectedChild(newChild);
      }
    } else {
      setLinkMessage({ type: 'error', text: result.message });
    }

    setLinking(false);
  };

  const handleUnlinkChild = async (childId) => {
    if (!window.confirm('Are you sure you want to unlink this child\'s account?')) {
      return;
    }

    const result = await unlinkChild(user.id, childId);
    if (result.success) {
      await loadChildren();
      if (selectedChild?.id === childId) {
        setSelectedChild(null);
        setChildProgress(null);
        setUpcomingEvents(null);
      }
    } else {
      alert(result.message);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString();
    }
    return new Date(timestamp).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="parent-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="parent-dashboard">
      <div className="parent-header">
        <h1>👨‍👩‍👧‍👦 Parent Dashboard</h1>
        <p>Monitor your child's learning progress and achievements</p>
      </div>

      {children.length === 0 && !showLinkForm && (
        <div className="empty-state">
          <div className="empty-icon">👶</div>
          <h2>No Children Linked Yet</h2>
          <p>Link your child's account to start tracking their progress</p>
          <button 
            className="primary-btn"
            onClick={() => setShowLinkForm(true)}
          >
            Link Child Account
          </button>
        </div>
      )}

      {showLinkForm && (
        <div className="link-form-card">
          <h2>Link Child Account</h2>
          <p className="form-description">
            Enter your child's email address to link their student account to your parent account.
          </p>
          <form onSubmit={handleLinkChild}>
            <div className="form-group">
              <label htmlFor="childEmail">Child's Email Address</label>
              <input
                type="email"
                id="childEmail"
                value={linkEmail}
                onChange={(e) => setLinkEmail(e.target.value)}
                placeholder="child@example.com"
                required
                disabled={linking}
              />
            </div>
            {linkMessage.text && (
              <div className={`message ${linkMessage.type}`}>
                {linkMessage.text}
              </div>
            )}
            <div className="form-actions">
              <button
                type="submit"
                className="primary-btn"
                disabled={linking}
              >
                {linking ? 'Linking...' : 'Link Account'}
              </button>
              <button
                type="button"
                className="secondary-btn"
                onClick={() => {
                  setShowLinkForm(false);
                  setLinkEmail('');
                  setLinkMessage({ type: '', text: '' });
                }}
                disabled={linking}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {children.length > 0 && (
        <>
          <div className="children-selector">
            <h3>Select Child</h3>
            <div className="children-list">
              {children.map((child) => (
                <div
                  key={child.id}
                  className={`child-card ${selectedChild?.id === child.id ? 'active' : ''}`}
                  onClick={() => setSelectedChild(child)}
                >
                  <div className="child-avatar">
                    {child.name?.charAt(0).toUpperCase() || '👤'}
                  </div>
                  <div className="child-info">
                    <h4>{child.name || child.email?.split('@')[0] || 'Student'}</h4>
                    <p>{child.email}</p>
                  </div>
                  <button
                    className="unlink-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnlinkChild(child.id);
                    }}
                    title="Unlink account"
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                className="add-child-btn"
                onClick={() => setShowLinkForm(true)}
                title="Link another child"
              >
                + Add Child
              </button>
            </div>
          </div>

          {selectedChild && childProgress && (
            <div className="child-dashboard">
              <div className="progress-overview">
                <div className="stat-card">
                  <div className="stat-icon">⭐</div>
                  <div className="stat-content">
                    <h3>{childProgress.totalPoints || 0}</h3>
                    <p>Total Points</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🏆</div>
                  <div className="stat-content">
                    <h3>{childProgress.badges || 0}</h3>
                    <p>Badges Earned</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">📚</div>
                  <div className="stat-content">
                    <h3>{childProgress.registeredSessions || 0}</h3>
                    <p>Registered Sessions</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🔥</div>
                  <div className="stat-content">
                    <h3>{childProgress.attendanceStreak || 0}</h3>
                    <p>Day Streak</p>
                  </div>
                </div>
              </div>

              {childProgress.badgeDetails && childProgress.badgeDetails.length > 0 && (
                <div className="badges-section">
                  <h3>Recent Badges</h3>
                  <div className="badges-grid">
                    {childProgress.badgeDetails.slice(0, 6).map((badge) => (
                      <div key={badge.id} className="badge-item">
                        <div className="badge-icon">{badge.emoji || '🏅'}</div>
                        <div className="badge-info">
                          <h4>{badge.name || 'Badge'}</h4>
                          <p>{badge.description || ''}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {upcomingEvents && upcomingEvents.upcomingSessions.length > 0 && (
                <div className="upcoming-section">
                  <h3>Upcoming Sessions</h3>
                  <div className="events-list">
                    {upcomingEvents.upcomingSessions.map((session) => (
                      <div key={session.id} className="event-item">
                        <div className="event-date">
                          {formatDate(session.startDate)}
                        </div>
                        <div className="event-details">
                          <h4>{session.title}</h4>
                          {session.description && (
                            <p>{session.description.substring(0, 100)}...</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="actions-section">
                <button
                  className="primary-btn"
                  onClick={() => window.open(`/student?child=${selectedChild.id}`, '_blank')}
                >
                  View Child's Dashboard
                </button>
              </div>
            </div>
          )}

          {selectedChild && (
            <div className="messaging-section">
              <ParentChildMessaging
                parentId={user.id}
                selectedChildId={selectedChild.id}
                selectedChildName={selectedChild.name || selectedChild.email?.split('@')[0] || 'Student'}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ParentDashboard;

